import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useOrgData } from '../hooks/useOrgData';
import { createBrand, loadBrands, updateProduct, updateStage, type BrandRow } from '../lib/data';
import { uploadEvidencia, uploadMockup } from '../lib/photos';
import { approveGate, loadGates, revokeGate, type GateApproval } from '../lib/gates';
import { addNote, deleteNote, updateNote, loadNotes, type StageNote } from '../lib/notes';
import { createNotifications, loadPeople, type Person } from '../lib/notifications';
import { loadEncaminhamentosForProduct, type Discussion } from '../lib/encaminhamentos';
import { DiscussionThread } from '../components/DiscussionThread';
import { canEditStage, canManageProducts, isAdmin } from '../lib/rbac';
import { FloatingBack } from '../components/FloatingBack';
import { computeProductSummary } from '../shared/criticalPath';
import { buildTemplateForType } from '../shared/terceirizacao';
import type {
  Phase,
  Priority,
  ProjectCategory,
  ProjectLifecycle,
  StageStatus,
} from '../shared/types';
import {
  CATEGORY_LABELS,
  SELECTABLE_CATEGORIES,
  LIFECYCLE_LABELS,
  PRIORITY_LABELS,
  StatusBadge,
  Progress,
  desvioLabel,
  fmtDate,
  metaInfo,
  STAGE_STATUS_LABEL,
} from '../components/ui';

const PHASES: Phase[] = ['PD1', 'PD2', 'PD3', 'PD4', 'PD5'];
const STATUSES: StageStatus[] = ['nao_iniciado', 'em_andamento', 'concluido'];
const PRIORITIES: Priority[] = ['alta', 'media', 'baixa'];
const LIFECYCLES: ProjectLifecycle[] = ['ativo', 'pausado', 'concluido', 'cancelado'];
const todayISO = () => new Date().toISOString().slice(0, 10);

export function Produto() {
  const { id } = useParams();
  const { currentOrgId, grants, areas, userId, fullName } = useAuth();
  const { loading, template, products } = useOrgData(currentOrgId);
  const [bulkPhase, setBulkPhase] = useState<Phase | null>(null);
  const [savingSit, setSavingSit] = useState(false);
  const [brands, setBrands] = useState<BrandRow[]>([]);
  const [gates, setGates] = useState<Record<string, GateApproval>>({});
  const [notes, setNotes] = useState<StageNote[]>([]);
  const [noteN, setNoteN] = useState(1);
  const [noteBody, setNoteBody] = useState('');
  const [noteFile, setNoteFile] = useState<File | null>(null);
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  const [savingNote, setSavingNote] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [encs, setEncs] = useState<Discussion[]>([]);
  // Fases cujas etapas concluídas estão expandidas (por padrão ficam recolhidas).
  const [openDone, setOpenDone] = useState<Set<Phase>>(new Set());
  const toggleDone = (ph: Phase) =>
    setOpenDone((s) => {
      const n = new Set(s);
      if (n.has(ph)) n.delete(ph);
      else n.add(ph);
      return n;
    });

  useEffect(() => {
    if (currentOrgId) loadPeople(currentOrgId).then(setPeople);
  }, [currentOrgId]);

  useEffect(() => {
    if (id) loadEncaminhamentosForProduct(id).then(setEncs);
  }, [id]);
  const reloadEncs = () => {
    if (id) loadEncaminhamentosForProduct(id).then(setEncs);
  };

  useEffect(() => {
    if (currentOrgId) loadBrands(currentOrgId).then(setBrands);
  }, [currentOrgId]);

  useEffect(() => {
    if (id) {
      loadGates(id).then(setGates);
      loadNotes(id).then(setNotes);
    }
  }, [id]);

  async function refreshGates() {
    if (id) setGates(await loadGates(id));
  }
  async function refreshNotes() {
    if (id) setNotes(await loadNotes(id));
  }

  const areaLabel = useMemo(() => {
    const m = new Map(areas.map((a) => [a.key, a.name]));
    return (k: string) => m.get(k) ?? k;
  }, [areas]);

  const product = products.find((p) => p.id === id);
  const summary = useMemo(
    () =>
      product
        ? computeProductSummary(
            product,
            buildTemplateForType(template, product.projectType ?? 'interno'),
            new Date(),
          )
        : null,
    [product, template],
  );

  if (loading) return <div className="center-note">Carregando…</div>;
  if (!product || !summary || !grants)
    return (
      <div className="center-note">
        Produto não encontrado. <Link to="/">Voltar ao resumo</Link>
      </div>
    );

  const stageByN = new Map(product.stages.map((s) => [s.n, s]));
  const computedByN = new Map(summary.stages.map((c) => [c.n, c]));
  const admin = isAdmin(grants);
  const canManage = canManageProducts(grants);
  const tpl = buildTemplateForType(template, product.projectType ?? 'interno');
  const terceirizado = (product.projectType ?? 'interno') === 'terceirizado';

  async function patchStage(n: number, patch: Parameters<typeof updateStage>[2]) {
    const { error } = await updateStage(product!.id, n, patch);
    if (error) alert(`Não foi possível salvar: ${error}`);
  }

  // Admin: conclui todas as etapas de uma fase de uma vez (o PMO/admin é isento
  // da trava de dependência do banco, então a fase pode ser fechada em bloco).
  async function completePhase(ph: Phase) {
    const stages = tpl.filter((t) => t.phase === ph);
    const pendentes = stages.filter((t) => stageByN.get(t.n)?.status !== 'concluido');
    if (pendentes.length === 0) return;
    if (
      !confirm(
        `Concluir todas as ${pendentes.length} etapa(s) da fase ${ph} de "${product!.name}"? ` +
          `Isso marca a fase inteira como concluída.`,
      )
    )
      return;
    setBulkPhase(ph);
    const results = await Promise.all(
      pendentes.map((t) => {
        const inst = stageByN.get(t.n);
        return updateStage(product!.id, t.n, {
          status: 'concluido',
          started_at: inst?.startedAt ?? todayISO(),
          finished_at: inst?.finishedAt ?? todayISO(),
        });
      }),
    );
    setBulkPhase(null);
    const firstErr = results.find((r) => r.error)?.error;
    if (firstErr) alert(`Algumas etapas não puderam ser concluídas: ${firstErr}`);
  }

  // Admin: define "em que pé está" o projeto em um clique. Escolhe a etapa atual
  // → conclui todas as anteriores, marca a atual em andamento e zera as demais.
  const orderedTpl = [...tpl].sort((a, b) => a.n - b.n);
  const tplNameByN = new Map(tpl.map((t) => [t.n, `${t.phase} · ${t.name}`]));
  const canComment = grants.role !== 'viewer';
  const peopleName = new Map(people.map((p) => [p.id, p.name]));
  const encAnchorN =
    summary.proximaEtapa?.n ??
    summary.stages.find((c) => c.status !== 'concluido')?.n ??
    summary.stages[0]?.n ??
    1;
  async function applySituacao(value: string) {
    if (!value) return;
    const targetN = value === 'none' ? 0 : value === 'all' ? 9999 : Number(value);
    const tplAlvo = orderedTpl.find((t) => t.n === targetN);
    const label =
      value === 'none'
        ? 'Não iniciado (nada concluído)'
        : value === 'all'
          ? 'Concluído (todas as etapas)'
          : `em andamento na etapa ${targetN}. ${tplAlvo?.name ?? ''}`;
    if (
      !confirm(
        `Definir a situação de "${product!.name}" como: ${label}?\n\n` +
          `Isso ajusta o status de TODAS as etapas de acordo.`,
      )
    )
      return;
    setSavingSit(true);
    const jobs: Promise<{ error?: string }>[] = [];
    for (const t of orderedTpl) {
      const inst = stageByN.get(t.n);
      const cur = inst?.status ?? 'nao_iniciado';
      let desired: StageStatus;
      if (value === 'all') desired = 'concluido';
      else if (value === 'none') desired = 'nao_iniciado';
      else desired = t.n < targetN ? 'concluido' : t.n === targetN ? 'em_andamento' : 'nao_iniciado';

      const datesConsistent =
        (desired === 'concluido' && !!inst?.finishedAt) ||
        (desired === 'em_andamento' && !!inst?.startedAt && !inst?.finishedAt) ||
        (desired === 'nao_iniciado' && !inst?.startedAt && !inst?.finishedAt);
      if (cur === desired && datesConsistent) continue;

      let patch: Parameters<typeof updateStage>[2];
      if (desired === 'concluido')
        patch = {
          status: 'concluido',
          started_at: inst?.startedAt ?? todayISO(),
          finished_at: inst?.finishedAt ?? todayISO(),
        };
      else if (desired === 'em_andamento')
        patch = { status: 'em_andamento', started_at: inst?.startedAt ?? todayISO(), finished_at: null };
      else patch = { status: 'nao_iniciado', started_at: null, finished_at: null };

      jobs.push(updateStage(product!.id, t.n, patch));
    }
    const res = await Promise.all(jobs);
    setSavingSit(false);
    const err = res.find((r) => r.error)?.error;
    if (err) alert(`Algumas etapas não puderam ser atualizadas: ${err}`);
  }

  async function setOpenedAt(v: string) {
    const { error } = await updateProduct(product!.id, { opened_at: v || null });
    if (error) alert(`Não foi possível salvar a data de abertura: ${error}`);
  }

  async function setPriority(v: Priority) {
    const { error } = await updateProduct(product!.id, { priority: v });
    if (error) alert(`Não foi possível salvar a prioridade: ${error}`);
  }

  async function setCategory(v: ProjectCategory) {
    const { error } = await updateProduct(product!.id, { category: v });
    if (error) alert(`Não foi possível salvar a categoria: ${error}`);
  }

  async function saveName(v: string) {
    const name = v.trim();
    if (!name || name === product!.name) return;
    const { error } = await updateProduct(product!.id, { name });
    if (error) alert(`Não foi possível renomear o projeto: ${error}`);
  }

  async function saveSap(v: string) {
    const sap = v.trim();
    if (sap === (product!.sapCode ?? '')) return;
    const { error } = await updateProduct(product!.id, { sap_code: sap || null });
    if (error) alert(`Não foi possível salvar o nº SAP: ${error}`);
  }

  async function setTargetLaunch(v: string) {
    const { error } = await updateProduct(product!.id, { target_launch: v || null });
    if (error) alert(`Não foi possível salvar a data-alvo: ${error}`);
  }

  async function changeBrand(v: string) {
    let brandId = v;
    if (v === '__new__') {
      const nome = prompt('Nome da nova marca:')?.trim();
      if (!nome || !currentOrgId) return;
      const { error, brand } = await createBrand(currentOrgId, nome);
      if (error || !brand) return alert(`Não foi possível criar a marca: ${error ?? ''}`);
      brandId = brand.id;
      setBrands((bs) => [...bs, brand].sort((a, b) => a.name.localeCompare(b.name)));
    }
    const { error } = await updateProduct(product!.id, { brand_id: brandId });
    if (error) alert(`Não foi possível mudar a marca: ${error}`);
  }

  async function changeLifecycle(v: ProjectLifecycle) {
    if (v === 'cancelado') {
      const motivo = prompt(
        'Motivo do cancelamento (fica registrado como aprendizado):',
        product!.cancelReason ?? '',
      );
      if (motivo === null) return; // cancelou o prompt
      const { error } = await updateProduct(product!.id, {
        lifecycle: 'cancelado',
        cancel_reason: motivo.trim() || null,
      });
      if (error) alert(`Não foi possível cancelar: ${error}`);
      return;
    }
    const { error } = await updateProduct(product!.id, {
      lifecycle: v,
      cancel_reason: null,
    });
    if (error) alert(`Não foi possível mudar o estado: ${error}`);
  }

  async function toggleGate(phase: string) {
    if (gates[phase]) {
      if (!confirm(`Revogar a aprovação do gate ${phase}?`)) return;
      const { error } = await revokeGate(product!.id, phase);
      if (error) return alert(error);
    } else {
      const note = prompt(`Aprovar a passagem de ${phase}? (observação opcional)`, '');
      if (note === null) return;
      const { error } = await approveGate({
        orgId: currentOrgId!,
        productId: product!.id,
        phase,
        userId,
        name: fullName,
        note: note.trim() || null,
      });
      if (error) return alert(error);
    }
    await refreshGates();
  }

  async function submitNote(e: React.FormEvent) {
    e.preventDefault();
    if (!noteBody.trim() && !noteFile) return;
    setSavingNote(true);
    let attachmentUrl: string | null = null;
    let attachmentName: string | null = null;
    if (noteFile) {
      const up = await uploadEvidencia(currentOrgId!, product!.id, noteN, noteFile);
      if (up.error) {
        setSavingNote(false);
        return alert(`Não foi possível anexar: ${up.error}`);
      }
      attachmentUrl = up.url ?? null;
      attachmentName = up.name ?? null;
    }
    const { error, id: noteId } = await addNote({
      orgId: currentOrgId!,
      productId: product!.id,
      n: noteN,
      authorId: userId,
      authorName: fullName,
      body: noteBody.trim() || null,
      attachmentUrl,
      attachmentName,
      mentionedIds: noteMentions,
    });
    if (!error) {
      const alvos = noteMentions.filter((uid) => uid !== userId);
      if (alvos.length) {
        const etapa = tplNameByN.get(noteN) ?? `etapa ${noteN}`;
        await createNotifications(
          alvos.map((uid) => ({
            orgId: currentOrgId!,
            userId: uid,
            productId: product!.id,
            noteId: noteId ?? null,
            fromName: fullName,
            text: `${fullName ?? 'Alguém'} mencionou você em um comentário — ${product!.brand} · ${product!.name} (${etapa}).`,
          })),
        );
      }
    }
    setSavingNote(false);
    if (error) return alert(error);
    setNoteBody('');
    setNoteFile(null);
    setNoteMentions([]);
    await refreshNotes();
  }

  async function removeNote(noteId: string) {
    if (!confirm('Remover este comentário/evidência?')) return;
    const { error } = await deleteNote(noteId);
    if (error) return alert(error);
    await refreshNotes();
  }

  function startEditNote(nt: StageNote) {
    setEditingNote(nt.id);
    setEditBody(nt.body ?? '');
  }

  async function saveEditNote(noteId: string) {
    const { error } = await updateNote(noteId, { body: editBody.trim() || null });
    if (error) return alert(error);
    setEditingNote(null);
    setEditBody('');
    await refreshNotes();
  }

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !currentOrgId) return;
    setUploadingPhoto(true);
    const { error, url } = await uploadMockup(currentOrgId, product!.id, file);
    if (error) alert(`Não foi possível enviar a imagem: ${error}`);
    else if (url) {
      const r = await updateProduct(product!.id, { photo_url: url });
      if (r.error) alert(r.error);
    }
    setUploadingPhoto(false);
  }

  // Linha de uma etapa (reutilizada para pendentes e concluídas).
  const renderStageRow = (st: (typeof tpl)[number]) => {
    const inst = stageByN.get(st.n);
    const comp = computedByN.get(st.n);
    const editable = canEditStage(grants!, st.areaKeys);
    return (
      <div className="stage-row" key={st.n}>
        <div className="muted tabular">{st.n}</div>
        <div className="stage-name">
          {st.name}
          <span className="resp">{st.areaKeys.map(areaLabel).join(' · ')}</span>
        </div>
        <div className="desktop-only">
          <select
            value={inst?.status ?? 'nao_iniciado'}
            disabled={!editable}
            onChange={(e) => {
              const status = e.target.value as StageStatus;
              const patch: Parameters<typeof updateStage>[2] = { status };
              if (status === 'concluido') {
                patch.started_at = inst?.startedAt ?? todayISO();
                patch.finished_at = inst?.finishedAt ?? todayISO();
              } else if (status === 'em_andamento') {
                patch.started_at = inst?.startedAt ?? todayISO();
                patch.finished_at = null;
              } else {
                patch.started_at = null;
                patch.finished_at = null;
              }
              patchStage(st.n, patch);
            }}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STAGE_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="desktop-only">
          <input
            type="number"
            min={1}
            defaultValue={inst?.daysPlanned ?? st.defaultDays}
            key={`d-${st.n}-${inst?.daysPlanned ?? st.defaultDays}`}
            disabled={!canManage}
            title="Prazo planejado (dias) — recalcula a previsão de lançamento"
            onBlur={(e) => {
              const v = Math.max(1, Math.round(Number(e.target.value) || 0));
              if (v && v !== (inst?.daysPlanned ?? st.defaultDays))
                patchStage(st.n, { days_planned: v });
            }}
          />
        </div>
        <div className="desktop-only">
          <input
            type="date"
            defaultValue={inst?.startedAt ?? ''}
            disabled={!editable}
            onBlur={(e) =>
              e.target.value !== (inst?.startedAt ?? '') &&
              patchStage(st.n, { started_at: e.target.value || null })
            }
          />
        </div>
        <div className="desktop-only">
          <input
            type="date"
            defaultValue={inst?.finishedAt ?? ''}
            disabled={!editable}
            onBlur={(e) =>
              e.target.value !== (inst?.finishedAt ?? '') &&
              patchStage(st.n, { finished_at: e.target.value || null })
            }
          />
        </div>
        <div className="desktop-only tabular">
          {fmtDate(comp?.predictedFinish ?? null)}
          {comp?.isLate && <span className="warn"> · atraso {comp.atrasoDias} d</span>}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="topbar">
        <div className="proj-avatar-wrap">
          {product.photoUrl ? (
            <img className="proj-avatar" src={product.photoUrl} alt="Mockup do produto" />
          ) : (
            <div className="proj-avatar ph" aria-hidden="true">📦</div>
          )}
          {canManage && (
            <label className="avatar-edit">
              {uploadingPhoto ? 'enviando…' : product.photoUrl ? 'trocar' : 'adicionar foto'}
              <input type="file" accept="image/*" hidden onChange={onPickPhoto} />
            </label>
          )}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            <Link to="/">Painel</Link> ·{' '}
            {canManage ? (
              <select
                className="brand-inline"
                value={product.brandId ?? ''}
                onChange={(e) => changeBrand(e.target.value)}
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
                <option value="__new__">＋ Nova marca…</option>
              </select>
            ) : (
              product.brand
            )}
          </div>
          {canManage ? (
            <input
              className="title-input"
              defaultValue={product.name}
              key={product.name}
              onBlur={(e) => saveName(e.target.value)}
              aria-label="Nome do projeto"
            />
          ) : (
            <h1>{product.name}</h1>
          )}
          <label className="field-inline" style={{ marginTop: 4 }}>
            <span className="muted">Nº do projeto no SAP</span>
            {canManage ? (
              <input
                style={{ maxWidth: 150 }}
                defaultValue={product.sapCode ?? ''}
                key={product.sapCode ?? 'nosap'}
                placeholder="ex.: 61000255"
                onBlur={(e) => saveSap(e.target.value)}
              />
            ) : (
              <b>{product.sapCode ?? '—'}</b>
            )}
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {terceirizado && <span className="terc-badge">Terceirizado</span>}
            <Link to={`/produto/${product.id}/capa`} className="btn-primary btn-sm" style={{ display: 'inline-block', textDecoration: 'none' }}>
              Abrir capa do projeto →
            </Link>
            {canManage && (
              <label className="field-inline">
                <span className="muted">Estado</span>
                <select
                  className={`lifecycle-select lc-${product.lifecycle ?? 'ativo'}`}
                  value={product.lifecycle ?? 'ativo'}
                  onChange={(e) => changeLifecycle(e.target.value as ProjectLifecycle)}
                >
                  {LIFECYCLES.map((l) => (
                    <option key={l} value={l}>
                      {LIFECYCLE_LABELS[l]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {product.lifecycle === 'cancelado' && (
            <div className="cancel-banner">
              Projeto cancelado{product.cancelReason ? ` — ${product.cancelReason}` : ''}.
            </div>
          )}
        </div>
        <div className="stat-strip">
          <div className="stat-chip">
            <span className="n">{summary.faseAtual}</span>
            <span className="l">Fase atual</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular">{Math.round(summary.percentConcluido * 100)}%</span>
            <span className="l">Concluído</span>
          </div>
          <div className="stat-chip">
            <span className="n tabular" style={{ fontSize: 13 }}>
              {fmtDate(summary.previsaoConclusao)}
            </span>
            <span className="l">Previsão</span>
          </div>
          <div className={`stat-chip ${summary.desvioDias > 0 ? 'warn' : ''}`}>
            <span className="n tabular">{desvioLabel(summary.desvioDias)}</span>
            <span className="l">Desvio</span>
          </div>
          {summary.targetLaunch && (
            <div className={`stat-chip ${(summary.desvioMeta ?? 0) > 0 ? 'warn' : ''}`}>
              <span className="n tabular" style={{ fontSize: 13 }}>
                {metaInfo(summary.desvioMeta).text}
              </span>
              <span className="l">Meta {fmtDate(summary.targetLaunch)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="content">
        <div className="card card-pad section-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <StatusBadge status={summary.status} />
            <Progress value={summary.percentConcluido} />
          </div>
          <div className="filters" style={{ alignItems: 'center' }}>
            <label className="field-inline">
              <span className="muted">Prioridade</span>
              <select
                defaultValue={product.priority ?? 'media'}
                disabled={!canManageProducts(grants)}
                onChange={(e) => setPriority(e.target.value as Priority)}
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-inline">
              <span className="muted">Categoria</span>
              <select
                defaultValue={product.category ?? 'inovacao'}
                disabled={!canManageProducts(grants)}
                onChange={(e) => setCategory(e.target.value as ProjectCategory)}
              >
                {(SELECTABLE_CATEGORIES.includes(product.category ?? 'inovacao')
                  ? SELECTABLE_CATEGORIES
                  : [product.category as ProjectCategory, ...SELECTABLE_CATEGORIES]
                ).map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-inline">
              <span className="muted">Data de abertura</span>
              <input
                type="date"
                defaultValue={product.openedAt ?? ''}
                disabled={!canManage}
                onBlur={(e) =>
                  e.target.value !== (product.openedAt ?? '') && setOpenedAt(e.target.value)
                }
              />
            </label>
            <label className="field-inline">
              <span className="muted">Meta de lançamento</span>
              <input
                type="date"
                defaultValue={product.targetLaunch ?? ''}
                key={product.targetLaunch ?? 'none'}
                disabled={!canManage}
                onBlur={(e) =>
                  e.target.value !== (product.targetLaunch ?? '') && setTargetLaunch(e.target.value)
                }
              />
            </label>
            {admin && (
              <label className="field-inline">
                <span className="muted">Situação atual (admin)</span>
                <select
                  value=""
                  disabled={savingSit}
                  onChange={(e) => {
                    const v = e.target.value;
                    e.currentTarget.value = '';
                    void applySituacao(v);
                  }}
                >
                  <option value="">{savingSit ? 'Aplicando…' : '— definir etapa atual —'}</option>
                  <option value="none">Não iniciado</option>
                  {orderedTpl.map((t) => (
                    <option key={t.n} value={String(t.n)}>
                      {t.phase} · {t.n}. {t.name}
                    </option>
                  ))}
                  <option value="all">Concluído (todas)</option>
                </select>
              </label>
            )}
          </div>
        </div>

        {canManage && (
          <p className="muted" style={{ fontSize: 12.5, margin: '2px 2px 10px' }}>
            💡 Ajuste o <b>Prazo (dias)</b> de cada etapa conforme este projeto (ex.: compra de
            embalagens com prazo diferente) — a <b>previsão de lançamento recalcula sozinha</b>.
          </p>
        )}

        {PHASES.map((ph) => {
          const stages = tpl.filter((t) => t.phase === ph);
          if (!stages.length) return null;
          const pendentes = stages.filter((t) => stageByN.get(t.n)?.status !== 'concluido');
          const concluidas = stages.filter((t) => stageByN.get(t.n)?.status === 'concluido');
          const doneOpen = openDone.has(ph);
          return (
            <div className="phase-block" key={ph}>
              <div className="phase-head">
                <span className="tag">{ph}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {stages.length} etapa(s)
                </span>
                {admin &&
                  stages.some((t) => stageByN.get(t.n)?.status !== 'concluido') && (
                    <button
                      className="btn-ghost btn-sm phase-complete"
                      disabled={bulkPhase !== null}
                      onClick={() => completePhase(ph)}
                      title={`Concluir toda a fase ${ph} (somente admin)`}
                    >
                      {bulkPhase === ph ? 'Concluindo…' : `✓ Concluir ${ph}`}
                    </button>
                  )}
                <span className="gate-slot">
                  {gates[ph] ? (
                    <span className="gate-badge" title={gates[ph]?.note ?? ''}>
                      ✓ Gate aprovado
                      {gates[ph]?.approvedByName ? ` · ${gates[ph]?.approvedByName}` : ''} ·{' '}
                      {fmtDate(gates[ph]?.approvedAt ?? null)}
                      {canManage && (
                        <button className="linklike" style={{ marginLeft: 8 }} onClick={() => toggleGate(ph)}>
                          revogar
                        </button>
                      )}
                    </span>
                  ) : (
                    canManage && (
                      <button className="btn-ghost btn-sm gate-btn" onClick={() => toggleGate(ph)}>
                        Aprovar passagem {ph} →
                      </button>
                    )
                  )}
                </span>
              </div>
              {(pendentes.length > 0 || doneOpen) && (
                <div className="stage-row head">
                  <div>#</div>
                  <div>Etapa</div>
                  <div className="desktop-only">Status</div>
                  <div className="desktop-only">Prazo (d)</div>
                  <div className="desktop-only">Início real</div>
                  <div className="desktop-only">Fim real</div>
                  <div className="desktop-only">Previsão / atraso</div>
                </div>
              )}
              {pendentes.map(renderStageRow)}
              {concluidas.length > 0 && (
                <>
                  <button
                    className="done-toggle"
                    onClick={() => toggleDone(ph)}
                    title={doneOpen ? 'Recolher concluídas' : 'Ver etapas concluídas e datas'}
                  >
                    <span className="exp-caret">{doneOpen ? '▾' : '▸'}</span>
                    {concluidas.length} etapa(s) concluída(s)
                    {!doneOpen && <span className="muted"> — clique para ver as datas</span>}
                  </button>
                  {doneOpen && concluidas.map(renderStageRow)}
                </>
              )}
            </div>
          );
        })}

        {/* Comentários & evidências (opcional) */}
        <div className="card card-pad">
          <div className="section-title">
            <h2>Comentários &amp; evidências</h2>
            <span className="muted" style={{ fontSize: 12 }}>opcional</span>
          </div>
          {canComment && (
            <form onSubmit={submitNote} className="note-form">
              <select
                value={noteN}
                onChange={(e) => setNoteN(Number(e.target.value))}
                style={{ maxWidth: 320 }}
              >
                {orderedTpl.map((t) => (
                  <option key={t.n} value={t.n}>
                    {t.phase} · {t.n}. {t.name}
                  </option>
                ))}
              </select>
              <textarea
                className="note-textarea"
                rows={3}
                placeholder="Escreva seu comentário / observação…"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
              />
              {people.length > 1 && (
                <div className="note-mentions">
                  <span className="muted" style={{ fontSize: 12, marginRight: 4 }}>Marcar pessoas:</span>
                  <span className="chip-toggle" style={{ flexWrap: 'wrap' }}>
                    {people
                      .filter((p) => p.id !== userId)
                      .map((p) => (
                        <button
                          type="button"
                          key={p.id}
                          className={noteMentions.includes(p.id) ? 'on' : ''}
                          onClick={() =>
                            setNoteMentions((m) =>
                              m.includes(p.id) ? m.filter((x) => x !== p.id) : [...m, p.id],
                            )
                          }
                        >
                          @{p.name}
                        </button>
                      ))}
                  </span>
                </div>
              )}
              <div className="note-actions">
                <label className="note-clip" title="Anexar arquivo">
                  📎 {noteFile ? noteFile.name : 'Anexar arquivo'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setNoteFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                {noteFile && (
                  <button type="button" className="linklike" onClick={() => setNoteFile(null)}>
                    remover anexo
                  </button>
                )}
                <button className="btn-primary btn-sm" disabled={savingNote} style={{ marginLeft: 'auto' }}>
                  {savingNote ? 'Enviando…' : 'Postar comentário'}
                </button>
              </div>
            </form>
          )}
          {notes.length === 0 ? (
            <p className="muted" style={{ marginTop: 8 }}>Nenhum comentário ou evidência ainda.</p>
          ) : (
            <div className="note-list">
              {notes.map((nt) => (
                <div className="note-item" key={nt.id}>
                  <div className="note-head">
                    <span className="tag-mini">{tplNameByN.get(nt.n) ?? `Etapa ${nt.n}`}</span>
                    <span className="muted" style={{ fontSize: 11.5 }}>
                      {nt.authorName ?? '—'} ·{' '}
                      {new Date(nt.createdAt).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                    {(nt.authorId === userId || canManage) && editingNote !== nt.id && (
                      <>
                        <button className="linklike" onClick={() => startEditNote(nt)}>editar</button>
                        <button className="linklike" onClick={() => removeNote(nt.id)}>remover</button>
                      </>
                    )}
                  </div>
                  {editingNote === nt.id ? (
                    <div className="note-edit">
                      <textarea
                        className="note-textarea"
                        rows={3}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                      />
                      <div className="note-actions">
                        <button className="btn-primary btn-sm" onClick={() => saveEditNote(nt.id)}>
                          Salvar alteração
                        </button>
                        <button className="linklike" onClick={() => setEditingNote(null)}>cancelar</button>
                      </div>
                    </div>
                  ) : (
                    nt.body && <div className="note-body">{nt.body}</div>
                  )}
                  {nt.mentionedIds.length > 0 && (
                    <div className="note-marcados">
                      📌 Acionado: {nt.mentionedIds.map((mid) => peopleName.get(mid) ?? '—').join(', ')}
                    </div>
                  )}
                  {nt.attachmentUrl && (
                    <a className="note-attach" href={nt.attachmentUrl} target="_blank" rel="noreferrer">
                      📎 {nt.attachmentName || nt.attachmentUrl}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {encs.length > 0 && (
          <div className="card card-pad">
            <div className="section-title">
              <h2>Encaminhamentos deste projeto</h2>
              <span className="muted" style={{ fontSize: 12 }}>pendências acionadas na reunião</span>
            </div>
            {encs.map((e) => (
              <DiscussionThread
                key={e.id}
                d={e}
                userId={userId}
                fullName={fullName}
                isAdmin={admin}
                anchorN={encAnchorN}
                onChanged={reloadEncs}
                showProjectLink={false}
              />
            ))}
          </div>
        )}
      </div>
      <FloatingBack to="/" label="Voltar ao painel" />
    </>
  );
}
